import { useEffect, useId, useRef, useState } from "react";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { OnboardingAnswers } from "../../../types/onboarding";
import styles from "./FertilityJourneyTypesQuestion.module.css";

interface FertilityJourneyTypesQuestionProps {
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string | string[]) => void;
	showValidation?: boolean;
}

export function FertilityJourneyTypesQuestion({
	answers,
	onInputChange,
	showValidation = false,
}: FertilityJourneyTypesQuestionProps) {
	const { t, direction } = useClientTranslations("onboarding");
	const { logEvent } = useAnalytics();
	const groupId = useId();
	const hasInitialized = useRef(false);

	const currentSelections = Array.isArray(answers.fertilityJourneyTypes)
		? answers.fertilityJourneyTypes
		: [];

	// Extract existing "Other - ..." text if present
	const existingOtherEntry = currentSelections.find((v) =>
		v.startsWith("Other - "),
	);
	const initialOtherText = existingOtherEntry
		? existingOtherEntry.replace("Other - ", "")
		: "";

	const [otherText, setOtherText] = useState(initialOtherText);

	// Initialize otherText from existing data on mount
	useEffect(() => {
		if (!hasInitialized.current && initialOtherText) {
			setOtherText(initialOtherText);
			hasInitialized.current = true;
		}
	}, [initialOtherText]);

	const handleToggleOption = (value: string) => {
		// Remove any existing "Other" or "Other - ..." entries
		const selectionsWithoutOther = currentSelections.filter(
			(v) => v !== "Other" && !v.startsWith("Other - "),
		);

		const isCurrentlySelected =
			currentSelections.includes(value) ||
			currentSelections.some((v) => v.startsWith("Other - "));

		// Check if we're at max selections (3) and trying to add more
		const MAX_SELECTIONS = 3;
		if (!isCurrentlySelected && currentSelections.length >= MAX_SELECTIONS) {
			// Don't allow selecting more than 3 options
			return;
		}

		let newSelections: string[];
		if (value === "Other") {
			if (isCurrentlySelected) {
				// Deselecting "Other"
				newSelections = selectionsWithoutOther;
				setOtherText("");
			} else {
				// Selecting "Other"
				newSelections = [...selectionsWithoutOther, value];
			}
		} else {
			// Toggle non-Other options normally
			newSelections = currentSelections.includes(value)
				? currentSelections.filter((v) => v !== value)
				: [...currentSelections, value];
		}

		onInputChange("fertilityJourneyTypes", newSelections);

		logEvent(
			isCurrentlySelected ? "journey_type_deselected" : "journey_type_selected",
			undefined,
			{
				field_name: "fertilityJourneyTypes",
				question_type: "fertility-journey-types",
				selected_value: value,
				total_selected: newSelections.length,
			},
		);
	};

	const handleOtherTextChange = (text: string) => {
		setOtherText(text);

		// Update the array: remove old "Other" or "Other - ..." and add new "Other - <text>"
		const selectionsWithoutOther = currentSelections.filter(
			(v) => v !== "Other" && !v.startsWith("Other - "),
		);

		const newValue = text.trim() ? `Other - ${text.trim()}` : "Other";
		const newSelections = [...selectionsWithoutOther, newValue];

		onInputChange("fertilityJourneyTypes", newSelections);

		logEvent("journey_type_other_text_entered", undefined, {
			field_name: "fertilityJourneyTypes",
			question_type: "fertility-journey-types",
			text_length: text.length,
		});
	};

	const hasError = showValidation && currentSelections.length === 0;
	const MAX_SELECTIONS = 3;
	const isMaxReached = currentSelections.length >= MAX_SELECTIONS;

	const journeyTypeOptions = [
		{ value: "IUI", label: t("questions.fertilityPath.pathOptions.iui") },
		{ value: "IVF", label: t("questions.fertilityPath.pathOptions.ivf") },
		{
			value: "Surrogacy",
			label: t("questions.fertilityPath.pathOptions.surrogacy"),
		},
		{
			value: "Egg donation",
			label: t("questions.fertilityPath.pathOptions.eggDonation"),
		},
		{
			value: "Sperm donation",
			label: t("questions.fertilityPath.pathOptions.spermDonation"),
		},
		{
			value: "Egg freezing",
			label: t("questions.fertilityPath.pathOptions.eggFreezing"),
		},
		{
			value: "I don't know yet",
			label: t("questions.fertilityPath.dontKnow"),
		},
		{ value: "Other", label: t("questions.fertilityPath.other") },
	];

	return (
		<div className={styles.container} dir={direction}>
			{isMaxReached && (
				<div className={styles.maxSelectionsMessage}>
					{t("fertilityJourneyTypes.maxSelections")}
				</div>
			)}
			<fieldset className={styles.optionsList} id={groupId}>
				{journeyTypeOptions.map((option) => {
					const isSelected =
						option.value === "Other"
							? currentSelections.includes("Other") ||
								currentSelections.some((v) => v.startsWith("Other - "))
							: currentSelections.includes(option.value);
					const isDisabled = isMaxReached && !isSelected;

					return (
						<label
							key={option.value}
							className={`${styles.optionCard} ${
								isSelected ? styles.optionCardSelected : ""
							} ${hasError ? styles.optionCardError : ""} ${
								isDisabled ? styles.optionCardDisabled : ""
							}`}
							onClick={() => {
								if (isDisabled) {
									logEvent("journey_type_disabled_click", undefined, {
										field_name: "fertilityJourneyTypes",
										question_type: "fertility-journey-types",
										clicked_value: option.value,
										current_selections: currentSelections.join(","),
									});
								}
							}}
							onKeyDown={(e) => {
								if (isDisabled && (e.key === "Enter" || e.key === " ")) {
									logEvent("journey_type_disabled_click", undefined, {
										field_name: "fertilityJourneyTypes",
										question_type: "fertility-journey-types",
										clicked_value: option.value,
										current_selections: currentSelections.join(","),
									});
								}
							}}
						>
							<input
								type="checkbox"
								checked={isSelected}
								onChange={() => handleToggleOption(option.value)}
								className={styles.hiddenCheckbox}
								aria-label={option.label}
								disabled={isDisabled}
							/>
							<div
								className={`${styles.checkbox} ${
									isSelected ? styles.checkboxSelected : ""
								}`}
							>
								{isSelected && (
									<svg
										width="12"
										height="9"
										viewBox="0 0 12 9"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
										className={styles.checkmark}
										role="img"
										aria-label="Selected"
									>
										<path
											d="M1 4.5L4.5 8L11 1"
											stroke="white"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								)}
							</div>
							<span className={styles.optionLabel}>{option.label}</span>
						</label>
					);
				})}
			</fieldset>
			{(currentSelections.includes("Other") ||
				currentSelections.some((v) => v.startsWith("Other - "))) && (
				<div className={styles.otherInputWrapper}>
					<input
						type="text"
						value={otherText}
						onChange={(e) => handleOtherTextChange(e.target.value)}
						placeholder={t("questions.fertilityPath.otherJourneyPlaceholder")}
						className={styles.otherInput}
						aria-label={t("questions.fertilityPath.otherJourneyPlaceholder")}
					/>
				</div>
			)}
		</div>
	);
}
